import { useI18n } from '#imports'
import { acceptHMRUpdate, defineStore } from 'pinia'

export const useSystemStore = defineStore('system', () => {
  const { t } = useI18n()
  const loading = ref(false)
  const loadingComment = ref('')
  const rail = ref(true)
  const drawer = ref(false)
  const adminLogin = ref(false)
  const guideCurrentView = ref<'list' | 'detail'>('list')

  const clientDrawerRight = ref(false)
  const clientDrawerRightMenuList = computed(() => [
    { title: t('navigation.creator'), color: 'white' },
    { title: t('navigation.brand'), color: 'white' },
    { title: t('navigation.reviews'), color: 'white' },
    { title: t('navigation.about'), color: 'white' },
    { title: t('navigation.mypage'), color: 'white' },
  ])
  const search = ref()
  const searchFocus = ref(false)
  const appBarHeight = ref(0)

  // 스낵바 관련 상태
  const snackbar = ref({
    isVisible: false,
    message: '',
    color: 'primary',
    timeout: 3000,
    location: 'bottom' as 'top' | 'bottom' | 'left' | 'right' | 'center',
    multiLine: false,
    vertical: false,
    closable: false,
    icon: '',
    actionText: '',
    actionColor: 'white',
  })

  // 다이얼로그 관련 상태
  const dialog = ref({
    isVisible: false,
    title: '',
    message: '',
    color: 'primary',
    icon: '',
    variant: 'flat' as 'flat' | 'tonal' | 'outlined' | 'text' | 'plain',
  })

  // 스낵바 관련 메서드
  function showSnackbar(options: {
    message: string
    color?: string
    timeout?: number
    location?: 'top' | 'bottom' | 'left' | 'right' | 'center'
    multiLine?: boolean
    vertical?: boolean
    closable?: boolean
    icon?: string
    actionText?: string
    actionColor?: string
  }) {
    Object.assign(snackbar.value, {
      isVisible: true,
      message: options.message,
      color: options.color || 'primary',
      timeout: options.timeout || 3000,
      location: options.location || 'top',
      multiLine: options.multiLine || false,
      vertical: options.vertical || false,
      closable: options.closable || false,
      icon: options.icon || '',
      actionText: options.actionText || '',
      actionColor: options.actionColor || 'white',
    })
  }

  function hideSnackbar() {
    snackbar.value.isVisible = false
  }

  // 다이얼로그 표시 메서드
  function showDialog(options: {
    title: string
    message: string
    color?: string
    icon?: string
    variant?: 'flat' | 'tonal' | 'outlined' | 'text' | 'plain'
  }) {
    Object.assign(dialog.value, {
      isVisible: true,
      title: options.title,
      message: options.message,
      color: options.color || 'primary',
      icon: options.icon || '',
      variant: options.variant || 'flat',
    })
  }

  function hideDialog() {
    dialog.value.isVisible = false
  }

  function showSuccess(message: string, _options?: Partial<Parameters<typeof showSnackbar>[0]>) {
    console.warn('showSuccess', message)
    showDialog({
      title: '성공',
      message,
      color: 'success',
      icon: 'mdi-check-circle',
      variant: 'flat',
    })
  }

  function showError(message: string, _options?: Partial<Parameters<typeof showSnackbar>[0]>) {
    console.warn('showError', message)
    showDialog({
      title: '오류',
      message,
      color: 'error',
      icon: 'mdi-alert-circle',
      variant: 'flat',
    })
  }

  function showWarning(message: string, _options?: Partial<Parameters<typeof showSnackbar>[0]>) {
    console.warn('showWarning', message)
    showDialog({
      title: '경고',
      message,
      color: 'warning',
      icon: 'mdi-alert',
      variant: 'flat',
    })
  }

  function showInfo(message: string, _options?: Partial<Parameters<typeof showSnackbar>[0]>) {
    console.warn('showInfo', message)
    showDialog({
      title: '정보',
      message,
      color: 'info',
      icon: 'mdi-information',
      variant: 'flat',
    })
  }

  return {
    loading,
    loadingComment,
    rail,
    drawer,
    adminLogin,
    clientDrawerRight,
    clientDrawerRightMenuList,
    search,
    searchFocus,
    snackbar,
    dialog,
    appBarHeight,
    guideCurrentView,
    showSnackbar,
    hideSnackbar,
    showDialog,
    hideDialog,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useSystemStore, import.meta.hot))
